import React, { useEffect, useState } from 'react';
import { getMainAxios } from '../utils/util';

/**
 * 체결 시점의 시장상황 팝업.
 *
 * 주문에서 체결까지 왜 수십 초씩 걸렸는지 보려면 두 가지를 나란히 봐야 한다:
 *   1) 주문이 본 가격    - kwbar1m (1분 집계. 시간당 잡이 만들어 최대 1시간 지연 가능)
 *   2) 그 시각 실제 시장 - kwfill(초 단위 체결틱) + kwdeptime(WebSocket 호가)
 * 둘의 괴리가 지연/슬리피지의 단서다.
 */

interface BarRow { tm: string; o: number; h: number; l: number; c: number; v: number; cntrstr?: number; }
interface TickRow { tm: string; prc: number; vol: number; cntrstr?: number; }
interface DepthRow {
  tm: string;
  askPrc1: number; askQty1: number; askPrc2: number; askQty2: number; askPrc3: number; askQty3: number;
  bidPrc1: number; bidQty1: number; bidPrc2: number; bidQty2: number; bidPrc3: number; bidQty3: number;
  totAskQty: number; totBidQty: number;
}
interface MarketAtRes {
  prodId: string; prodNm: string; d: string; baseTm: string; windowSec: number;
  bars: BarRow[]; ticks: TickRow[]; depth: DepthRow[];
}

interface Props {
  prodId: string;
  prodNm?: string;
  d: string;          // yyyy-MM-dd
  tm: string;         // HHmmss - 체결 시각
  fillPrc?: number;   // 체결가 (있으면 주문가와 대조해 보여준다)
  onClose: () => void;
}

const fmtTm = (t?: string) =>
  (t && t.length >= 6 ? t.slice(0, 2) + ':' + t.slice(2, 4) + ':' + t.slice(4, 6) : t ?? '');
const n = (v?: number | null) => (v == null ? '-' : v.toLocaleString());

const MarketAtDialog: React.FC<Props> = ({ prodId, prodNm, d, tm, fillPrc, onClose }) => {
  const [data, setData] = useState<MarketAtRes | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [win, setWin] = useState(60);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    getMainAxios()
      .get<MarketAtRes>('/api/v1/pfbs/market-at', { params: { prodId, d, tm, windowSec: win } })
      .then(res => { if (alive) { setData(res.data); setLoading(false); } })
      .catch(e => { if (alive) { setErr(e?.message ?? '조회 실패'); setLoading(false); } });
    return () => { alive = false; };
  }, [prodId, d, tm, win]);

  // 체결 시각에 가장 가까운 행을 찾아 강조한다.
  const nearest = <T extends { tm: string }>(rows: T[]): string | null => {
    if (!rows || rows.length === 0) return null;
    const target = Number(tm);
    let best = rows[0];
    let diff = Math.abs(Number(rows[0].tm) - target);
    for (const r of rows) {
      const dd = Math.abs(Number(r.tm) - target);
      if (dd < diff) { diff = dd; best = r; }
    }
    return best.tm;
  };

  const hlTick = data ? nearest(data.ticks) : null;
  const hlDepth = data ? nearest(data.depth) : null;
  const hlBar = data ? nearest(data.bars) : null;

  const th: React.CSSProperties = {
    padding: '4px 6px', borderBottom: '1px solid #e5e7eb', textAlign: 'right',
    fontWeight: 600, color: '#1e40af', position: 'sticky', top: 0, backgroundColor: '#eff6ff',
  };
  const td: React.CSSProperties = { padding: '3px 6px', borderBottom: '1px solid #f3f4f6', textAlign: 'right' };
  const box: React.CSSProperties = {
    border: '2px solid #3b82f6', borderRadius: '8px', padding: '10px',
    backgroundColor: 'white', display: 'flex', flexDirection: 'column', minHeight: 0,
  };
  const cap: React.CSSProperties = { fontSize: '12px', fontWeight: 600, color: '#1e40af', marginBottom: '6px' };
  const hl: React.CSSProperties = { backgroundColor: '#fef3c7', fontWeight: 600 };

  // 주문가(1분봉 종가) 대비 실제 체결가 괴리
  const barRow = data ? data.bars.find(b => b.tm === hlBar) : undefined;
  const barC = barRow ? barRow.c : undefined;
  const gap = (barC != null && fillPrc != null) ? fillPrc - barC : null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#f8fafc', borderRadius: '10px', padding: '16px',
          width: 'min(1180px, 96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#1e40af' }}>
              {prodNm || (data ? data.prodNm : '')} ({prodId})
            </span>
            <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '10px' }}>
              {d} {fmtTm(tm)} 체결 시점
            </span>
            {fillPrc != null && (
              <span style={{ fontSize: '12px', color: '#111827', marginLeft: '10px' }}>
                체결가 <b>{n(fillPrc)}</b>
              </span>
            )}
            {gap != null && (
              <span style={{
                fontSize: '12px', marginLeft: '8px',
                color: gap === 0 ? '#6b7280' : gap > 0 ? '#dc2626' : '#2563eb',
              }}>
                주문가(1분봉 {n(barC)}) 대비 {gap > 0 ? '+' : ''}{n(gap)}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11px', color: '#6b7280' }}>범위</span>
            <select
              value={win}
              onChange={e => setWin(Number(e.target.value))}
              style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
            >
              <option value={30}>30초</option>
              <option value={60}>1분</option>
              <option value={180}>3분</option>
              <option value={600}>10분</option>
            </select>
            <button
              onClick={onClose}
              style={{
                padding: '3px 12px', backgroundColor: '#3b82f6', color: 'white',
                border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer',
              }}
            >닫기</button>
          </div>
        </div>

        {loading && <div style={{ padding: '30px', textAlign: 'center', color: '#6b7280' }}>불러오는 중...</div>}
        {err && <div style={{ padding: '20px', color: '#dc2626', fontSize: '12px' }}>조회 실패: {err}</div>}

        {data && !loading && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr', gap: '10px', minHeight: 0, flex: 1 }}>
            {/* 1) 주문이 본 가격 */}
            <div style={box}>
              <div style={cap}>
                주문 가격 기준 <span style={{ fontWeight: 400, color: '#6b7280' }}>kwbar1m (1분봉)</span>
              </div>
              <div style={{ overflow: 'auto', fontSize: '11px', flex: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={{ ...th, textAlign: 'left' }}>시각</th>
                    <th style={th}>시가</th><th style={th}>고가</th>
                    <th style={th}>저가</th><th style={th}>종가</th><th style={th}>거래량</th>
                  </tr></thead>
                  <tbody>
                    {data.bars.length === 0 && (
                      <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: '#9ca3af' }}>데이터 없음</td></tr>
                    )}
                    {data.bars.map(b => (
                      <tr key={b.tm} style={b.tm === hlBar ? hl : undefined}>
                        <td style={{ ...td, textAlign: 'left' }}>{fmtTm(b.tm)}</td>
                        <td style={td}>{n(b.o)}</td><td style={td}>{n(b.h)}</td>
                        <td style={td}>{n(b.l)}</td><td style={td}>{n(b.c)}</td><td style={td}>{n(b.v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 2) 실제 체결 */}
            <div style={box}>
              <div style={cap}>
                실제 체결 <span style={{ fontWeight: 400, color: '#6b7280' }}>kwfill (초 단위)</span>
              </div>
              <div style={{ overflow: 'auto', fontSize: '11px', flex: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={{ ...th, textAlign: 'left' }}>시각</th>
                    <th style={th}>체결가</th><th style={th}>거래량</th><th style={th}>체결강도</th>
                  </tr></thead>
                  <tbody>
                    {data.ticks.length === 0 && (
                      <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#9ca3af' }}>데이터 없음</td></tr>
                    )}
                    {data.ticks.map((t, i) => (
                      <tr key={t.tm + '-' + i} style={t.tm === hlTick ? hl : undefined}>
                        <td style={{ ...td, textAlign: 'left' }}>{fmtTm(t.tm)}</td>
                        <td style={td}>{n(t.prc)}</td><td style={td}>{n(t.vol)}</td>
                        <td style={td}>{t.cntrstr != null ? t.cntrstr.toFixed(1) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 3) 호가 */}
            <div style={box}>
              <div style={cap}>
                호가 <span style={{ fontWeight: 400, color: '#6b7280' }}>kwdeptime (WebSocket)</span>
              </div>
              <div style={{ overflow: 'auto', fontSize: '11px', flex: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={{ ...th, textAlign: 'left' }}>시각</th>
                    <th style={{ ...th, color: '#2563eb' }}>매수3</th>
                    <th style={{ ...th, color: '#2563eb' }}>매수2</th>
                    <th style={{ ...th, color: '#2563eb' }}>매수1</th>
                    <th style={{ ...th, color: '#dc2626' }}>매도1</th>
                    <th style={{ ...th, color: '#dc2626' }}>매도2</th>
                    <th style={{ ...th, color: '#dc2626' }}>매도3</th>
                  </tr></thead>
                  <tbody>
                    {data.depth.length === 0 && (
                      <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#9ca3af' }}>데이터 없음</td></tr>
                    )}
                    {data.depth.map(dp => (
                      <tr key={dp.tm} style={dp.tm === hlDepth ? hl : undefined}>
                        <td style={{ ...td, textAlign: 'left' }}>{fmtTm(dp.tm)}</td>
                        <td style={{ ...td, color: '#2563eb' }}>
                          {n(dp.bidPrc3)}<br /><span style={{ color: '#9ca3af' }}>{n(dp.bidQty3)}</span>
                        </td>
                        <td style={{ ...td, color: '#2563eb' }}>
                          {n(dp.bidPrc2)}<br /><span style={{ color: '#9ca3af' }}>{n(dp.bidQty2)}</span>
                        </td>
                        <td style={{ ...td, color: '#2563eb', fontWeight: 600 }}>
                          {n(dp.bidPrc1)}<br /><span style={{ color: '#9ca3af', fontWeight: 400 }}>{n(dp.bidQty1)}</span>
                        </td>
                        <td style={{ ...td, color: '#dc2626', fontWeight: 600 }}>
                          {n(dp.askPrc1)}<br /><span style={{ color: '#9ca3af', fontWeight: 400 }}>{n(dp.askQty1)}</span>
                        </td>
                        <td style={{ ...td, color: '#dc2626' }}>
                          {n(dp.askPrc2)}<br /><span style={{ color: '#9ca3af' }}>{n(dp.askQty2)}</span>
                        </td>
                        <td style={{ ...td, color: '#dc2626' }}>
                          {n(dp.askPrc3)}<br /><span style={{ color: '#9ca3af' }}>{n(dp.askQty3)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: '8px', fontSize: '11px', color: '#6b7280' }}>
          노란 행 = 체결 시각에 가장 가까운 데이터 · 주문 수량은 <b>kwbar1m 종가</b>로 환산되고(시간당 갱신),
          체결은 <b>kwfill</b> 초 단위 틱으로 판정된다.
        </div>
      </div>
    </div>
  );
};

export default MarketAtDialog;
